// Block layer — base types.
//
// A Block is typed React content placed in a slot via the mapping. The full
// kit (text, stat, material-collections, catalog, group, etc.) lands in
// step 4; v1 of step 3 only introduces the `action` block since the
// editorial tool rail needs it.
//
// See .design.md "Axis 3: Block types".

import type { ActionMap } from "../actions/types";
import type { Overlay } from "../../../rules";
import type {
  DesignerSeriesDto,
  SceneCategoryDto,
  SceneInstance,
  SceneProductDetailResponse,
  SceneProductDto,
} from "../types";

/** Visibility condition — closed enum (see .design.md "showWhen"). */
export type ShowWhen =
  | "always"
  | "selection-present"
  | "selection-empty"
  | "scene-empty"
  | "scene-not-empty"
  | { feature: string; equals: boolean };

/** Universal props every block accepts (set on its slot-mapping entry). */
export interface BlockUniversalProps {
  /** Default "always". */
  showWhen?: ShowWhen;
  /** Theme-variant attribute exposed as data-variant on the rendered element. */
  themeVariant?: string;
  /** Escape-hatch class hook. Sparingly. */
  className?: string;
}

/**
 * A block placed in a slot. The shape is identical whether it lives in a
 * layout's hardcoded default mapping or in a Designer override JSON column
 * — both deserialise into this.
 *
 * `key` is unique within its parent slot (or within `children` for groups).
 * Used as the React key for reconciliation and as the override-match id for
 * the future per-Designer authoring UI.
 */
export interface BlockInstance {
  block: BlockKind;
  key: string;
  /** Default true. When false the block stays in the mapping but is skipped
   *  at render — lets an author park a block instead of deleting it. Only
   *  serialised when false to keep existing mappings byte-identical. */
  enabled?: boolean;
  props?: Record<string, unknown>;
  /** Universal showWhen lifted to the instance so the slot resolver can
   *  filter without instantiating the block component first. */
  showWhen?: ShowWhen;
  themeVariant?: string;
  className?: string;
  /** Only meaningful for `group` blocks — child block instances. */
  children?: BlockInstance[];
}

/** Closed enum of v1 block kinds. New kinds are commits — extend here.
 *
 *  Note: `cart-items` is in the design doc but deferred past step 4 — the
 *  cart modal stays rendered through CartModal's existing internal layout
 *  until we have a concrete second client whose cart-rows look different
 *  enough to motivate the extraction.
 *
 *  `divider` vs. `line`: divider is the *generic* hairline (an inline rule
 *  between items); `line` is the editorial separator with its own breathing
 *  room (used between major sections in single-menu). They render
 *  similarly but the spacing rules differ. */
export type BlockKind =
  | "action"
  | "text"
  | "stat"
  | "image"
  | "material-collections"
  | "selection-surfaces"
  | "catalog"
  | "cart-items"
  // Series-bound blocks for the composable series-picker tiles. They render
  // once per series (bound to the current series via SeriesContext) and only
  // mean anything inside the series-picker-tiles slot.
  | "series-image"
  | "series-name"
  | "series-description"
  | "divider"
  | "line"
  | "spacer"
  | "group"
  | "notification"
  // Form primitives — collect operator data into a shared form-state store
  // keyed by formId. input-*'s `name` prop names the field; form-submit's
  // `formId` selects which form to read + fires its actionId.
  | "input-text"
  | "input-email"
  | "input-textarea"
  | "form-submit"
  // Composable product table — mirrors the PDF pdf-product-table. A
  // `product-table` holds an ordered list of column groups (each a `group`
  // whose children render per row), plus sort + group config in its props.
  // The product-* kinds bind to the current row (read via RowContext) and
  // only render meaningfully inside a product-table cell / group header.
  | "product-table"
  | "product-image"
  | "product-name"
  | "product-parameter"
  | "product-sku"
  | "product-quantity"
  | "product-materials"
  | "product-price";

/** App events a `notification` block can subscribe to via its `trigger` prop.
 *  Fired through BlockHostContext.notify; the NotificationsHost matches blocks
 *  by trigger and shows their message as a top-of-screen toast. Closed enum —
 *  new triggers are commits, like showWhen / TextSource. */
export type NotificationTrigger =
  | "product-tap-no-drag"
  | "product-click-no-drag"
  | "drag-cancelled"
  | "scene-empty-on-cart";

/**
 * Slot mapping shape — slotId → ordered list of block instances. A layout's
 * default mapping has this shape; the Designer override JSON column does too.
 * The Slot component reads from the merged mapping at render time.
 */
export type SlotMapping = Record<string, BlockInstance[]>;

/**
 * Host context every block receives. Grows as blocks need it; new entries
 * are commits, not config. Block components read from this rather than from
 * SceneDesignerTool's internals directly so the seam stays narrow.
 */
export interface BlockHostContext {
  /** Live ActionMap — ActionBlock looks up by id. */
  actions: ActionMap;

  /** Selection + scene state. */
  selectedInstanceId: string | null;
  selectedInstance: SceneInstance | null;
  /** Display name of the selected product (resolved from the catalog). Null
   *  when no selection or when the catalog hasn't loaded yet. Used by the
   *  TextBlock's `from-selected-product-name` source and by the
   *  SelectionSurfacesBlock. */
  selectedProductName: string | null;
  instances: SceneInstance[];
  instanceCount: number;

  /** Catalog data (passed in lieu of a fetch — blocks render, never fetch). */
  productById: Map<string, SceneProductDto>;
  /** Categories visible in the current series scope — what CatalogBlock browses. */
  visibleCategories: SceneCategoryDto[];
  /** Flat product list scoped to the active series. */
  products: SceneProductDto[];

  /** Active series for the current configurator session. Null when no
   *  series is in scope (demo mode, single-series Designer before pick). */
  activeSeries: DesignerSeriesDto | null;

  /** Product-detail cache for blocks that show surfaces/materials. The Map is
   *  mutated in place (stable identity), so blocks that derive from it must key
   *  their memoisation on detailCacheVersion, not the Map reference. */
  detailCache: Map<string, SceneProductDetailResponse>;
  /** Bumps every time a detail is added to detailCache. Include it in a memo's
   *  deps so derived data (e.g. resolved material names) recomputes on warm. */
  detailCacheVersion: number;
  onCacheDetail: (detail: SceneProductDetailResponse) => void;

  /** sceneFeatures slice — typed reads. */
  getFeature: (key: string) => unknown;

  /** Modal triggers (cart-items rows might link to product detail later).
   *  Optional `payload` is stashed alongside the open-modal id so the
   *  modal's render override can read per-instance data the opening block
   *  carries — e.g. the render-scene modal needs the action block's
   *  renderProfileId. Each new open replaces the previous payload for
   *  that id; closing the modal clears it. */
  openModal: (id: string, payload?: unknown) => void;

  /** Push a confirm dialog. Used by ActionBlock to gate destructive verbs
   *  whose ActionDef carries a `confirm` payload. The host owns the
   *  modal state and renders the dialog; onConfirm fires only if the
   *  user accepts. Inert in focus-mode previews. */
  openConfirm: (opts: {
    title?: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    onConfirm: () => void;
  }) => void;

  /** baseUrl for any block that needs to fetch its own data (currently none,
   *  but Stat block's "price" kind will reach out for prices later). */
  baseUrl?: string;

  /** Short language code (e.g. "en", "dk") for blocks that fetch their own
   *  translated data (e.g. SelectionSurfacesBlock's per-product surface detail).
   *  Unknown/blank falls back to primary server-side. */
  lang?: string;

  /**
   * Apply a material to every placed instance whose product has a surface in
   * the given collection. Used by MaterialCollectionsBlock's double-tap to
   * "change all" — the host walks instances, looks up each product's surfaces
   * via the detail cache, and fires SetSurfaceMaterials per match.
   *
   * Surfaces whose product detail isn't cached yet are skipped — pragmatic
   * for v1; a fully thorough implementation would warm-fetch before applying.
   */
  applyMaterialToAll: (collectionId: string, materialId: string, dmatAssetId: string | null) => void;

  /** Apply a material to a single surface on the currently-selected instance.
   *  Used by SelectionSurfacesBlock when the operator taps a swatch in the
   *  on-selection popup. No-op when nothing is selected. */
  onPickMaterial: (surfaceKey: string, materialId: string) => void;

  /** Current default material pick per collection (collectionId →
   *  { materialId, dmatAssetId }). Drives the left-panel collection block's
   *  selected-swatch state and is read by the drag layer at drag-start to
   *  build SetSurfaceMaterials-shaped bindings keyed by surfaceKey via the
   *  product DTO's surfaceCollections map. dmatAssetId may be null when the
   *  picked material has no DMatAsset on the wire (rare; logs a warn). */
  collectionPicks: Record<string, { materialId: string; dmatAssetId: string | null }>;

  /** Set the default material for a collection. Drives future spawns; also
   *  applies immediately to the currently-selected instance's surfaces wired
   *  to this collection (matches the user's single-tap = set-default-and-
   *  apply-to-selected intent). No-op when materialId is empty. */
  onPickCollectionDefault: (collectionId: string, materialId: string, dmatAssetId: string | null) => void;

  /** Fire an app event that the notification system may show as a toast. The
   *  NotificationsHost subscribes; any `notification` block whose `trigger`
   *  matches surfaces its message. Optional (focus-mode previews omit it).
   *  Blocks call this from interaction handlers (e.g. the catalog on a
   *  tap-without-drag). Unknown triggers are simply ignored by the host. */
  notify?: (trigger: NotificationTrigger) => void;

  /** Rules-engine overlay from the latest evaluation — forced/locked/
   *  restricted picks + validation flags, keyed by instanceId (see
   *  src/rules/types.ts Overlay). Blocks read it to render constraints:
   *  SelectionSurfacesBlock disables locked rows / greys restricted swatches,
   *  FormSubmitBlock gates submit on error-severity flags. Null/absent when
   *  no rules are loaded (demo mode, fetch failure, focus previews) — blocks
   *  then render unconstrained, matching pre-rules behaviour. */
  rulesOverlay?: Overlay | null;

  /** Form-state store. Input blocks write to it on change, submit blocks
   *  read it on click. Keyed by (formId, fieldName). formId defaults to
   *  the slot id when the operator doesn't override it. */
  formValues: Record<string, Record<string, string>>;
  /** Write one field. Pass empty string to clear. */
  setFormValue: (formId: string, name: string, value: string) => void;
  /** Read every field of one form as a flat object. Submit blocks pass this
   *  through to the action via blockProps.formValues. */
  readFormValues: (formId: string) => Record<string, string>;
  /** Wipe one form's state — called after a successful submit so the next
   *  open starts blank. */
  clearForm: (formId: string) => void;
}

/** Result of evaluating a showWhen against current state — pure function. */
export function evaluateShowWhen(cond: ShowWhen | undefined, host: BlockHostContext): boolean {
  if (!cond || cond === "always") return true;
  if (cond === "selection-present") return host.selectedInstanceId != null;
  if (cond === "selection-empty") return host.selectedInstanceId == null;
  if (cond === "scene-empty") return host.instanceCount === 0;
  if (cond === "scene-not-empty") return host.instanceCount > 0;
  if (typeof cond === "object" && "feature" in cond) {
    return host.getFeature(cond.feature) === cond.equals;
  }
  // Unknown shape — be conservative (render). Logged once would be noisy
  // since this evaluates every render; silent default-to-true is fine.
  return true;
}
